/**
 * Read-only operator policy reference (DNKADM-0192 U2).
 *
 * The prose mirrors the canonical policy plan while every facility-level number
 * comes from the single DNKAPI-0319 public read. Operators can follow ownership
 * links to the existing editors; this screen never writes policy state.
 */
import { Fragment, useCallback, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  GlobalStyles,
  Link as MuiLink,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getPolicies } from '@/api/policyReference';
import { getClaims } from '@/auth/authStore';
import { useCurrentOperator } from '@/auth/authHooks';
import { useFrontDeskView } from '@/auth/frontDeskHooks';
import { useResource } from '@/hooks/useResource';
import { Icon } from '@/theme';
import { tenantSlugFromClaims } from '@/utils/jwt';
import { formatMoney } from '@/utils/v2Format';
import { scrubBrand } from '@/utils/whitelabel';
import { policyValueMapping, type PolicyValueKey } from './policyValueMappings';
import { ScreenError } from '@/components/ScreenError';

type AvailablePolicies = Extract<
  Awaited<ReturnType<typeof getPolicies>>,
  { status: 'available' }
>['data'];

const TOPICS = [
  {
    id: 'booking',
    title: 'Booking & Availability',
    summary: 'When members can book and what makes a time slot available.',
    icon: 'calendar_month',
  },
  {
    id: 'reservation',
    title: 'Your Reservation',
    summary: 'Booking statuses, check-in, attendance, and no-shows.',
    icon: 'event_available',
  },
  {
    id: 'cancellations',
    title: 'Cancellations & Reschedules',
    summary: 'Refund deadlines, rescheduling, and anti-gaming safeguards.',
    icon: 'event_busy',
  },
  {
    id: 'credits',
    title: 'Credits',
    summary: 'How credit buckets, lots, spending, refunds, and limits work.',
    icon: 'toll',
  },
  {
    id: 'memberships',
    title: 'Memberships & Plans',
    summary: 'Renewals, grace periods, deposits, and plan cancellation.',
    icon: 'workspace_premium',
  },
  {
    id: 'packages',
    title: 'Packages & One-time Purchases',
    summary: 'How non-recurring credit bundles are bought and expire.',
    icon: 'inventory_2',
  },
  {
    id: 'payments',
    title: 'Payments, Pricing & Tax',
    summary: 'Currency, pricing rules, tax, card-on-file, and fees.',
    icon: 'payments',
  },
  {
    id: 'guests',
    title: 'Guests & Guest Checkout',
    summary: 'Bringing guests and paying without an account.',
    icon: 'group_add',
  },
  {
    id: 'privacy',
    title: 'Your Data, Privacy & Communications',
    summary: 'What is stored, how identity is protected, and what can be erased.',
    icon: 'shield_person',
  },
] as const;

type TopicId = (typeof TOPICS)[number]['id'];

const PRINT_STYLES = {
  '@media print': {
    'body *': { visibility: 'hidden !important' },
    '[data-policy-print-root], [data-policy-print-root] *': {
      visibility: 'visible !important',
    },
    '[data-policy-print-root]': {
      position: 'absolute !important',
      inset: '0 auto auto 0 !important',
      width: '100% !important',
      // Print output is deliberately mode-INDEPENDENT: paper is white, ink is
      // black, whatever the on-screen theme (DNKADM-0269 — keywords, not theme
      // tokens, because this never renders on screen).
      background: 'white !important',
      color: 'black !important',
    },
    '.policy-no-print': { display: 'none !important' },
    '.policy-print-all': { display: 'block !important' },
    '.MuiAccordion-root': {
      breakInside: 'avoid',
      boxShadow: 'none !important',
      border: '0 !important',
    },
    '.MuiCollapse-root': {
      height: 'auto !important',
      visibility: 'visible !important',
    },
    '.MuiCollapse-wrapper, .MuiCollapse-wrapperInner': {
      display: 'block !important',
    },
    '.MuiAccordionSummary-expandIconWrapper': { display: 'none !important' },
    '.MuiAccordionDetails-root': { display: 'block !important' },
    table: { breakInside: 'avoid' },
  },
} as const;

export function PolicyReferenceScreen() {
  const tenantSlug = tenantSlugFromClaims(getClaims());
  const policies = useResource(
    useCallback(() => {
      if (!tenantSlug) return Promise.reject(new Error('Missing tenant slug'));
      return getPolicies(tenantSlug);
    }, [tenantSlug]),
    [tenantSlug],
  );
  const [selectedTopic, setSelectedTopic] = useState<TopicId | null>(null);

  if (policies.status === 'loading') {
    return (
      <Skeleton
        variant="rounded"
        height={420}
        role="status"
        aria-label="Loading policies"
        sx={{ borderRadius: 3 }}
      />
    );
  }

  if (policies.status === 'error' || !policies.data) {
    return <ScreenError error={policies.error} what="policies" onRetry={policies.refetch} />;
  }

  if (policies.data.status === 'unavailable') {
    return (
      <Alert severity="info">
        Policies are unavailable for this facility. Confirm that the tenant is active and fully
        provisioned.
      </Alert>
    );
  }

  const bundle = policies.data.data;
  const selected = TOPICS.find((topic) => topic.id === selectedTopic) ?? null;

  return (
    <Box data-policy-print-root>
      <GlobalStyles styles={PRINT_STYLES} />
      {selected ? (
        <>
          <Stack
            className="policy-no-print"
            direction="row"
            sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}
          >
            <Button
              color="inherit"
              startIcon={<Icon name="arrow_back" />}
              onClick={() => setSelectedTopic(null)}
            >
              Back to all policies
            </Button>
            <Button
              variant="outlined"
              startIcon={<Icon name="print" />}
              onClick={() => window.print()}
            >
              Print this topic
            </Button>
          </Stack>
          <TopicContent topic={selected.id} bundle={bundle} />
        </>
      ) : (
        <Landing bundle={bundle} onSelect={setSelectedTopic} />
      )}
    </Box>
  );
}

function Landing({
  bundle,
  onSelect,
}: {
  bundle: AvailablePolicies;
  onSelect: (topic: TopicId) => void;
}) {
  return (
    <>
      <Stack
        className="policy-no-print"
        direction="row"
        sx={{
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography variant="h1">Policies</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 760 }}>
            A read-only guide to how this facility handles bookings, credits, memberships, and
            money. Live values match the member-facing policy source; follow a Configurable link to
            make changes in its owning screen.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Icon name="print" />} onClick={() => window.print()}>
          Print all policies
        </Button>
      </Stack>

      <Box
        className="policy-no-print"
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 2,
        }}
      >
        {TOPICS.map((topic) => (
          <Card key={topic.id} variant="outlined" sx={{ height: '100%' }}>
            <CardActionArea onClick={() => onSelect(topic.id)} sx={{ height: '100%' }}>
              <CardContent sx={{ minHeight: 150 }}>
                <Icon name={topic.icon} sx={{ color: 'primary.main', fontSize: 30 }} />
                <Typography variant="h3" sx={{ mt: 1.5 }}>
                  {topic.title}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.75 }}>
                  {topic.summary}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      <Box className="policy-print-all" sx={{ display: 'none' }}>
        <Typography variant="h1" sx={{ mb: 2 }}>
          Facility policies
        </Typography>
        {TOPICS.map((topic) => (
          <Box key={topic.id} sx={{ mb: 4, breakBefore: topic.id === 'booking' ? 'auto' : 'page' }}>
            <TopicContent topic={topic.id} bundle={bundle} />
          </Box>
        ))}
      </Box>
    </>
  );
}

function TopicContent({ topic, bundle }: { topic: TopicId; bundle: AvailablePolicies }) {
  const metadata = TOPICS.find((item) => item.id === topic);
  return (
    <Box component="article" aria-labelledby={`policy-topic-${topic}`}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1.25, mb: 0.75 }}>
        <Icon name={metadata?.icon ?? 'gavel'} sx={{ color: 'primary.main', fontSize: 30 }} />
        <Typography id={`policy-topic-${topic}`} variant="h1">
          {metadata?.title}
        </Typography>
      </Stack>
      <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
        {metadata?.summary}
      </Typography>
      {topic === 'booking' && <BookingPolicy bundle={bundle} />}
      {topic === 'reservation' && <ReservationPolicy bundle={bundle} />}
      {topic === 'cancellations' && <CancellationPolicy bundle={bundle} />}
      {topic === 'credits' && <CreditsPolicy />}
      {topic === 'memberships' && <MembershipPolicy bundle={bundle} />}
      {topic === 'packages' && <PackagesPolicy />}
      {topic === 'payments' && <PaymentsPolicy bundle={bundle} />}
      {topic === 'guests' && <GuestsPolicy bundle={bundle} />}
      {topic === 'privacy' && <PrivacyPolicy />}
    </Box>
  );
}

function BookingPolicy({ bundle }: { bundle: AvailablePolicies }) {
  return (
    <PolicySections>
      <PolicySection title="How far ahead and how slots are shown">
        <ValueRule
          label="Booking window"
          value={numberValue(
            bundle.booking.windowMonths,
            (value) => `Members can book from now up to ${value} months ahead.`,
          )}
          mappingKey="booking.windowMonths"
          linkLabel="Change booking window"
        />
        <ValueRule
          label="Service interval"
          value={numberValue(
            bundle.booking.serviceIntervalMinutes,
            (value) => `Available starts appear on a ${value}-minute grid.`,
          )}
          mappingKey="booking.serviceIntervalMinutes"
          linkLabel="Change service interval"
        />
        <RuleText>
          Each service option has its own duration and may reserve setup and cleanup buffers. There
          is no minimum lead-time rule: a later-today slot remains bookable until it starts. Setup
          and cleanup buffers default to 0 minutes.
        </RuleText>
        <OwnerRule
          label="Duration and buffers"
          value="Configured per service option; buffers may be zero."
          route="/services"
          linkLabel="Choose a service"
        />
        <PlatformRule label="Minimum booking lead time" value="None." />
      </PolicySection>
      <PolicySection title="What makes a slot bookable">
        <RuleText>
          A slot appears only when the location is open and every required staff member and resource
          is eligible, scheduled, free, and available for the full appointment plus its buffers. A
          missing required staff or resource assignment never becomes unlimited availability.
        </RuleText>
        <OwnerRule
          label="Location hours"
          value="Configured for each location, including exceptions."
          mappingKey="location.hours"
          linkLabel="Choose a location for hours"
        />
        <OwnerRule
          label="Resource capacity"
          value="One simultaneous reservation by default; shared spaces can be higher."
          route="/resources"
          linkLabel="Choose a resource"
        />
        <OwnerRule
          label="Staff appointment caps"
          value="No cap unless one is set for a day, week, or month; weekly caps reset Sunday."
          route="/staff"
          linkLabel="Choose a staff member"
        />
      </PolicySection>
      <PolicySection title="Member access and local time">
        <RuleText>
          Guests and non-members see times inside posted location hours. Active members may also
          book when staff or resources are scheduled beyond public hours; real coverage and capacity
          still apply. All times and date calculations use the location’s IANA time zone.
        </RuleText>
        <PlatformRule
          label="Member after-hours booking"
          value="Active membership widens the window but never removes availability checks."
        />
        <PlatformRule
          label="Slot grid and overnight shifts"
          value="The slot grid is anchored at midnight, and overnight shifts are not supported."
        />
        <OwnerRule
          label="Location time zone"
          value="Configured per location."
          route="/locations"
          linkLabel="Choose a location for its time zone"
        />
      </PolicySection>
    </PolicySections>
  );
}

function ReservationPolicy({ bundle }: { bundle: AvailablePolicies }) {
  return (
    <PolicySections>
      <PolicySection title="Reservation lifecycle">
        <RuleText>
          Upcoming means a confirmed future booking. In progress covers the appointment and its
          check-in window. Awaiting reconciliation means the time passed without attendance being
          recorded, so the member can no longer cancel or reschedule. Reconciled means staff
          recorded attended or no-show; Cancelled is closed; Hold is a temporary guest-payment
          reservation.
        </RuleText>
        <ValueRule
          label="Reconciliation grace"
          value={numberValue(
            bundle.reservation.reconciliationGraceMinutes,
            (value) => `${value} minutes after the appointment ends.`,
          )}
          mappingKey="reservation.reconciliationGraceMinutes"
        />
      </PolicySection>
      <PolicySection title="Check-in and no-shows">
        <RuleText>
          A member can check themselves in to a confirmed booking but cannot mark themselves a
          no-show. Staff can mark attended or no-show and reverse that decision for a late arrival;
          cancelled or voided bookings cannot be marked.
        </RuleText>
        <OwnerRule
          label="No-show fee"
          value="Configured per variation for card-on-file bookings; when unset, the no-show charge defaults to the booked variation price. Credit bookings carry no extra fee."
          mappingKey="variation.noShowFee"
          linkLabel="Choose a service for variation fees"
        />
        <ValueRule
          label="Guest/payment hold expiry"
          value={numberValue(bundle.booking.guestHoldTtlMinutes, (value) => `${value} minutes.`)}
          mappingKey="booking.guestHoldTtlMinutes"
          linkLabel="Change guest hold time"
        />
      </PolicySection>
      <StaffCallout>
        Staff can mark or unmark no-show and attended. For a card-on-file booking, they can waive,
        charge now, or adjust the deferred pending charge during its management window. Waiving a
        no-show fee when marking a no-show needs a manager to enter the PIN.
      </StaffCallout>
    </PolicySections>
  );
}

function CancellationPolicy({ bundle }: { bundle: AvailablePolicies }) {
  const maxReschedules =
    bundle.membership.maxReschedules === null
      ? 'Unlimited.'
      : `${bundle.membership.maxReschedules}.`;
  return (
    <PolicySections>
      <PolicySection title="Cancellation refunds">
        <RuleText>
          Refunds depend on how far before the start a member cancels. Earlier cancellations can
          return more. Credits go back to the same lots with their original expiration, never more
          than was spent. The slot is released even if refund processing must retry.
        </RuleText>
        <ValueRule
          label="Cancellation bands"
          value="Facility-wide defaults and credit-type exceptions are evaluated by threshold."
          mappingKey="cancellation.bands"
          linkLabel="Edit cancellation bands"
        />
        <ValueRule
          label="Default full-refund threshold"
          value={numberValue(
            bundle.cancellation.defaultFullRefundMinHours,
            (value) =>
              `When the facility has no configured cancellation bands, cancelling at least ${value} hours ahead returns 100%; cancelling later returns 0%.`,
          )}
          mappingKey="cancellation.defaultFullRefundMinHours"
        />
        <CancellationBands bundle={bundle} />
        <ValueRule
          label="Refund rounding"
          value={roundingValue(
            bundle.cancellation.refundRoundingIncrement,
            bundle.cancellation.refundRoundingMode,
          )}
          mappingKey="cancellation.refundRoundingIncrement"
          linkLabel="Change refund rounding"
        />
      </PolicySection>
      <PolicySection title="Rescheduling and past bookings">
        <RuleText>
          Rescheduling moves the appointment without charging or refunding credits. If a booking is
          moved after its full-refund window, it becomes permanently refund-locked; cancelling it
          later returns nothing. This prevents moving a late booking merely to regain a refund.
        </RuleText>
        <ValueRule
          label="Maximum reschedules"
          value={maxReschedules}
          mappingKey="membership.maxReschedules"
          linkLabel="Change maximum reschedules"
        />
        <ValueRule
          label="Past-booking cutoff"
          value={numberValue(
            bundle.reservation.reconciliationGraceMinutes,
            (value) => `Cancellation and rescheduling stop after the end plus ${value} minutes.`,
          )}
          mappingKey="reservation.reconciliationGraceMinutes"
        />
        <PlatformRule
          label="Refund lock"
          value="Moving after the full-refund window permanently locks the member refund."
        />
      </PolicySection>
      <StaffCallout>
        Staff can force a full refund, apply the policy result, or give no refund when cancelling,
        including overriding the reschedule refund-lock.
      </StaffCallout>
    </PolicySections>
  );
}

function CancellationBands({ bundle }: { bundle: AvailablePolicies }) {
  const bands = [...bundle.cancellation.bands].sort(
    (a, b) => b.minHoursBefore - a.minHoursBefore || b.refundPercent - a.refundPercent,
  );
  if (bands.length === 0) {
    return (
      <Alert severity="info" aria-label="Default cancellation fallback" sx={{ my: 1.5 }}>
        {numberValue(
          bundle.cancellation.defaultFullRefundMinHours,
          (value) =>
            `With no configured bands, cancelling ${value} hours or more before the start returns 100%; cancelling later returns 0%.`,
        )}
      </Alert>
    );
  }

  const defaultBands = bands.filter((band) => band.scope === 'ALL');
  const exceptionGroups = new Map<string, typeof bands>();
  for (const band of bands) {
    if (band.scope !== 'BUCKET') continue;
    const label = policyBandLabel(band);
    const group = exceptionGroups.get(label) ?? [];
    group.push(band);
    exceptionGroups.set(label, group);
  }

  return (
    <TableContainer>
      <Table aria-label="Cancellation refund bands" size="small" sx={{ my: 1.5 }}>
        <TableHead>
          <TableRow>
            <TableCell>Applies to</TableCell>
            <TableCell>Cancel at least</TableCell>
            <TableCell align="right">Credits back</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {defaultBands.length > 0 ? (
            <TableRow>
              <TableCell colSpan={3} sx={{ fontWeight: 700, bgcolor: 'action.hover' }}>
                Default — all credit types
              </TableCell>
            </TableRow>
          ) : null}
          {defaultBands.map((band, index) => (
            <PolicyBandRow key={`all-${band.minHoursBefore}-${index}`} band={band} />
          ))}
          {[...exceptionGroups.entries()].map(([label, group]) => (
            <Fragment key={label}>
              <TableRow>
                <TableCell colSpan={3} sx={{ fontWeight: 700, bgcolor: 'action.hover' }}>
                  Exception — {label}
                </TableCell>
              </TableRow>
              {group.map((band, index) => (
                <PolicyBandRow
                  key={`${band.bucketId ?? label}-${band.minHoursBefore}-${index}`}
                  band={band}
                />
              ))}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function PolicyBandRow({ band }: { band: AvailablePolicies['cancellation']['bands'][number] }) {
  return (
    <TableRow>
      <TableCell>{band.scope === 'ALL' ? 'All credit types' : policyBandLabel(band)}</TableCell>
      <TableCell>{band.minHoursBefore} hours</TableCell>
      <TableCell align="right">{band.refundPercent}%</TableCell>
    </TableRow>
  );
}

function CreditsPolicy() {
  return (
    <PolicySections>
      <PolicySection title="Buckets and lots">
        <RuleText>
          Credits live in named buckets and each service draws from exactly one bucket; balances do
          not cross between credit types. Grants arrive as lots with their own expiration or no
          expiration. Booking spends the soonest-expiring lots first, then the oldest, and one
          booking may draw from several lots. Lots may work everywhere or at one location.
        </RuleText>
        <OwnerRule
          label="Service credit bucket and price"
          value="Configured per service and service option."
          route="/services"
          linkLabel="Choose a service for its credit rules"
        />
        <PlatformRule
          label="Spend order"
          value="Soonest expiry first, then oldest; lots without expiry are last."
        />
      </PolicySection>
      <PolicySection title="Spending, refunds, and limits">
        <RuleText>
          Booking deducts the full fixed credit price immediately. If the balance is short, the
          booking fails and nothing is deducted. Cancellation returns the policy refund to the
          original lots and expiration. Balances never go negative. Daily caps, when configured,
          limit booked hours rather than credits. Expired lots stop counting; cleanup never changes
          the usable balance.
        </RuleText>
        <PlatformRule label="Credit precision" value="Two decimal places, rounded half-up." />
        <OwnerRule
          label="Daily booked-hours caps"
          value="No cap unless one is configured for a plan or credit bucket."
          route="/membership-credits/daily-caps"
          linkLabel="Manage daily caps"
        />
      </PolicySection>
      <StaffCallout>
        Staff can grant and reduce credit lots, adjust balances, and change lot expiry outside the
        normal purchase and renewal flow. Those actions remain auditable lot operations.
      </StaffCallout>
    </PolicySections>
  );
}

function MembershipPolicy({ bundle }: { bundle: AvailablePolicies }) {
  return (
    <PolicySections>
      <PolicySection title="Status, renewal, and grace">
        <RuleText>
          A member holds one plan at a time. Self-serve plans must be published and priced; unpriced
          tiers are manual or complimentary only. Recurring renewal is handled by the payment
          processor, deposits the cycle’s configured credits, and extends access. A member may
          cancel at period end or immediately and may reverse a scheduled cancellation before it
          takes effect. Cancellation does not immediately remove access: it continues through
          expiration plus grace. A membership with no expiration never lapses.
        </RuleText>
        <ValueRule
          label="Membership grace"
          value={numberValue(
            bundle.membership.graceDays,
            (value) =>
              `Access remains active through expiration plus ${value} grace days, inclusive.`,
          )}
          mappingKey="membership.graceDays"
          linkLabel="Change membership grace period"
        />
        <PlatformRule label="Plans held at once" value="One." />
        <OwnerRule
          label="Plan price, term, and publication"
          value="Configured on each plan."
          route="/membership-credits/plans"
          linkLabel="Manage plans"
        />
        <PlatformRule
          label="Lapsed membership"
          value="A lapse blocks new bookings, but existing credits remain until their own lots expire."
        />
      </PolicySection>
      <PolicySection title="Credits on renewal">
        <RuleText>
          Each renewal deposits exactly the plan’s explicit bucket grants. Replace-mode renewals
          clear active lots in the bucket before the fresh grant; additive plans stack new credits
          with leftovers. Renewal credits expire at the end of the term’s grace window.
        </RuleText>
        <OwnerRule
          label="Renewal deposits and replace/additive mode"
          value="Configured per plan and bucket; replace is the default."
          route="/membership-credits/plans"
          linkLabel="Manage plan credits"
        />
      </PolicySection>
      <PolicySection title="First-purchase Add-On credits">
        <RuleText>
          Add-On credits are granted only on a qualifying first purchase. By default they are
          granted once ever and never expire; a plan can require a lapse of several months before
          re-eligibility and can assign an expiry in days. Ordinary renewals leave Add-On lots
          untouched, even for a replace-mode plan.
        </RuleText>
        <OwnerRule
          label="Add-On eligibility and expiry"
          value="Configured for each plan grant."
          route="/membership-credits/plans"
          linkLabel="Manage Add-On credits"
        />
      </PolicySection>
      <StaffCallout>
        Staff can assign manual or complimentary memberships. An unpriced tier is manual-only, and a
        manual membership may have no expiration and therefore never lapse.
      </StaffCallout>
    </PolicySections>
  );
}

function PackagesPolicy() {
  return (
    <PolicySections>
      <PolicySection title="One purchase, one set of credit lots">
        <RuleText>
          A package is a one-time purchase, not a membership and not a renewal. Only an active,
          published, priced package is self-serve purchasable. Buying it deposits one lot for each
          configured bucket. The purchase itself does not expire; each granted lot has its own
          optional expiry. Packages carry no tax.
        </RuleText>
        <OwnerRule
          label="Package price, publication, grants, and expiry"
          value="Configured on the package and its bucket grants; grant expiry defaults to never."
          route="/membership-credits/packages"
          linkLabel="Manage packages"
        />
        <PlatformRule label="Tax on packages" value="None; the package price is the full amount." />
      </PolicySection>
    </PolicySections>
  );
}

function PaymentsPolicy({ bundle }: { bundle: AvailablePolicies }) {
  const zero = bundle.currency ? formatMoney(0, bundle.currency) : 'a zero-value authorization';
  return (
    <PolicySections>
      <PolicySection title="Currency and pricing">
        <ValueRule
          label="Facility currency"
          value={`${bundle.currency ?? 'Unavailable'}; it locks after the first money or booking activity.`}
          mappingKey="currency"
          linkLabel="Review facility currency"
        />
        <RuleText>
          A service price may vary by date, staff member, or day of week; the most specific price
          replaces, rather than stacks with, the base price. Members, staff, and guests use the same
          resolver. Price plus tax is snapshotted on the reservation, so later configuration does
          not rewrite the booked total.
        </RuleText>
        <PlatformRule label="Money precision" value="Two decimal places, rounded half-up." />
        <PlatformRule
          label="Supported currencies"
          value="The facility currency must use no more than two fraction digits."
        />
        <OwnerRule
          label="Service pricing and payment mode"
          value="Configured per service option."
          route="/services"
          linkLabel="Choose a service for pricing"
        />
      </PolicySection>
      <PolicySection title="Tax and ways to pay">
        <RuleText>
          Tax is exclusive and added to the service price. It is set per location, while each
          service is either taxable or not. Each named rate is rounded before the lines are summed.
          A named rate may include tax-ID and agency details shown on the receipt. Packages,
          memberships, and offerings carry no tax.
        </RuleText>
        <OwnerRule
          label="Service taxable setting"
          value="Configured per service."
          mappingKey="service.taxable"
          linkLabel="Choose a service for taxability"
        />
        <OwnerRule
          label="Tax rates"
          value="Configured per location."
          mappingKey="location.taxRates"
          linkLabel="Choose a location for tax rates"
        />
        <RuleText>
          Charge now is the default payment mode and collects price plus tax at booking. Credit
          bookings spend credits. Card-on-file bookings vault the card with {zero} charged up front,
          then charge the booked price after check-in or the configured fee after a no-show.
        </RuleText>
      </PolicySection>
      <PolicySection title="No-show fees and refunds">
        <OwnerRule
          label="No-show fee"
          value="An optional amount per variation for card-on-file bookings. Blank defaults to the booked variation price; 0 means no charge; credit bookings have no extra cash fee."
          mappingKey="variation.noShowFee"
          linkLabel="Choose a service for variation fees"
        />
        <ValueRule
          label="Deferred charge timing"
          value={numberValue(
            bundle.reservation.deferredChargeMinutes,
            (value) => `${value} minutes after the appointment ends.`,
          )}
          mappingKey="reservation.deferredChargeMinutes"
        />
        <RuleText>
          Paid-booking refunds are executed by the payment processor at the amount the application
          decides. Credit-booking refunds return credits under the cancellation policy.
        </RuleText>
      </PolicySection>
      <StaffCallout>
        Staff can waive, charge now, or adjust a card-on-file deferred charge during the pending
        window after attendance is marked.
      </StaffCallout>
      <ComingCallout>
        No-show fee preview and an adjustable amount at the moment staff marks a no-show are not yet
        active. Today, staff manage the post-mark pending charge.
      </ComingCallout>
    </PolicySections>
  );
}

function GuestsPolicy({ bundle }: { bundle: AvailablePolicies }) {
  return (
    <PolicySections>
      <PolicySection title="Bringing a guest">
        <RuleText>
          A member may bring guests for free: no extra charge, credit, or capacity cost. The number
          allowed is controlled per service; zero means unlimited.
        </RuleText>
        <OwnerRule
          label="Maximum guests"
          value="Configured per service; zero means unlimited."
          mappingKey="service.maxGuests"
          linkLabel="Choose a service for its guest limit"
        />
      </PolicySection>
      <PolicySection title="Guest checkout">
        <RuleText>
          A first-time guest can book and pay without an account. A confirmed card charge is the
          anti-abuse gate; cardless redirect payments require an emailed one-time code. The server
          computes the price and raw card details never touch this system. A decline releases the
          slot and a successful charge confirms it.
        </RuleText>
        <ValueRule
          label="Guest checkout hold"
          value={numberValue(bundle.booking.guestHoldTtlMinutes, (value) => `${value} minutes.`)}
          mappingKey="booking.guestHoldTtlMinutes"
          linkLabel="Change guest hold time"
        />
        <PlatformRule
          label="Payment-status checks"
          value="Limited to 60 checks per minute for each IP address."
        />
      </PolicySection>
    </PolicySections>
  );
}

function PrivacyPolicy() {
  return (
    <PolicySections>
      <PolicySection title="What is held and who owns it">
        <RuleText>
          Name, email, and phone are owned by the identity provider and the facility’s CRM and are
          read live. Email changes use a verified one-time-code flow and notify the old address.
          This system stores credit balances, membership, one optional JPEG profile photo, assigned
          coach, and configured profile fields. Credits and membership are the source of truth. A
          member may have one profile photo, stored outside the app as a 256×256 JPEG.
        </RuleText>
        <OwnerRule
          label="Member-editable profile fields"
          value="Private by default; an operator allowlist controls what members may edit."
          route="/member-profile-fields"
          linkLabel="Manage member profile fields"
        />
      </PolicySection>
      <PolicySection title="Privacy and communications">
        <RuleText>
          A member’s booking list is server-scoped to their authenticated identity; the browser
          never supplies trusted identity or authority. Appointment reminders and marketing are
          managed by the facility through its CRM. Credit totals and membership tier, status, and
          expiration mirror one-way to the CRM. The application itself sends only internal
          operational alerts to facility admins, not member marketing.
        </RuleText>
        <PlatformRule
          label="Booking visibility"
          value="A member can read only their own server-scoped bookings."
        />
      </PolicySection>
      <PolicySection title="Deletion">
        <RuleText>
          Today an admin can erase payment data by removing personal linkage while retaining the
          amounts needed for legal and financial records. That is anonymization, not full identity
          deletion.
        </RuleText>
      </PolicySection>
      <ComingCallout>
        CRM workflow triggers for renewal, purchase, and payment-failed events are not yet active.
        Full right-to-be-forgotten deletion is also not yet active; today only payment-data erasure
        is available.
      </ComingCallout>
    </PolicySections>
  );
}

function PolicySections({ children }: { children: ReactNode }) {
  return <Stack spacing={1.5}>{children}</Stack>;
}

function PolicySection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Accordion defaultExpanded disableGutters variant="outlined">
      <AccordionSummary expandIcon={<Icon name="expand_more" />}>
        <Typography variant="h3" component="span">
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.25}>{children}</Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function RuleText({ children }: { children: ReactNode }) {
  return <Typography variant="body1">{children}</Typography>;
}

function ValueRule({
  label,
  value,
  mappingKey,
  linkLabel,
}: {
  label: string;
  value: ReactNode;
  mappingKey: PolicyValueKey;
  linkLabel?: string;
}) {
  const mapping = policyValueMapping(mappingKey);
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
          <Typography variant="body2">{value}</Typography>
          {mapping.note ? (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
            >
              {mapping.note}
            </Typography>
          ) : null}
        </Box>
        <Chip
          size="small"
          label={mapping.chipLabel}
          color={mapping.classification === 'configurable' ? 'primary' : 'default'}
          variant="outlined"
        />
        {mapping.route && linkLabel ? (
          <OpenSettingLink route={mapping.route} linkLabel={linkLabel} />
        ) : null}
      </Stack>
    </Paper>
  );
}

function OwnerRule({
  label,
  value,
  route,
  linkLabel,
  mappingKey,
}: {
  label: string;
  value: ReactNode;
  route?: string;
  linkLabel: string;
  mappingKey?: PolicyValueKey;
}) {
  if (mappingKey) {
    return <ValueRule label={label} value={value} mappingKey={mappingKey} linkLabel={linkLabel} />;
  }
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
          <Typography variant="body2">{value}</Typography>
        </Box>
        <Chip size="small" label="Configurable" color="primary" variant="outlined" />
        <OpenSettingLink route={route ?? '/'} linkLabel={linkLabel} />
      </Stack>
    </Paper>
  );
}

function PlatformRule({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
          <Typography variant="body2">{value}</Typography>
        </Box>
        <Chip size="small" label="Platform rule" variant="outlined" />
      </Stack>
    </Paper>
  );
}

function OpenSettingLink({ route, linkLabel }: { route: string; linkLabel: string }) {
  const operator = useCurrentOperator();
  const { frontDeskView } = useFrontDeskView();
  if (operator?.isAdmin !== true || frontDeskView) return null;
  return (
    <MuiLink component={RouterLink} to={route} aria-label={linkLabel}>
      Open setting
    </MuiLink>
  );
}

function StaffCallout({ children }: { children: ReactNode }) {
  return (
    <Alert severity="success" icon={<Icon name="admin_panel_settings" />}>
      <Typography sx={{ fontWeight: 700, mb: 0.25 }}>What your staff can do</Typography>
      {children}
    </Alert>
  );
}

function ComingCallout({ children }: { children: ReactNode }) {
  return (
    <Alert severity="warning" icon={<Icon name="schedule" />}>
      <Typography sx={{ fontWeight: 700, mb: 0.25 }}>Coming — not yet active</Typography>
      {children}
    </Alert>
  );
}

function numberValue(value: number | null, available: (value: string) => string): string {
  return value === null ? "This value isn't available right now." : available(`${value}`);
}

function roundingValue(increment: number | null, mode: string | null): string {
  if (increment === null || mode === null) return "This value isn't available right now.";
  return `${increment} credits, ${mode.toLowerCase().replaceAll('_', ' ')}.`;
}

function policyBandLabel(band: AvailablePolicies['cancellation']['bands'][number]): string {
  return scrubBrand(band.bucketName) ?? 'Credit bucket exception';
}
